import { __extends } from "tslib";
import { PerspectiveProjection, CoordinateSystem, Vector3D, Transform, Matrix3D } from "@awayjs/core";
import { Stage, BitmapImage2D, _Stage_BitmapImage2D, ContextGLBlendFactor, ContextGLTriangleFace } from "@awayjs/stage";
import { RenderGroup, RendererType, Style } from "@awayjs/renderer";
import { DisplayObject } from "../display/DisplayObject";
import { DisplayObjectContainer } from "../display/DisplayObjectContainer";
import { SceneGraphPartition } from '../partition/SceneGraphPartition';
import { View } from '@awayjs/view';
import { MethodMaterial } from '@awayjs/materials';
import { Billboard } from '../display/Billboard';
/**
 *
 */
var SceneImage2D = /** @class */ (function (_super) {
    __extends(SceneImage2D, _super);
    /**
     * Creates a BitmapImage2D object with a specified width and height. If you
     * specify a value for the <code>fillColor</code> parameter, every pixel in
     * the bitmap is set to that color.
     *
     * <p>By default, the bitmap is created as transparent, unless you pass
     * the value <code>false</code> for the transparent parameter. After you
     * create an opaque bitmap, you cannot change it to a transparent bitmap.
     * Every pixel in an opaque bitmap uses only 24 bits of color channel
     * information. If you define the bitmap as transparent, every pixel uses 32
     * bits of color channel information, including an alpha transparency
     * channel.</p>
     *
     * @param width       The width of the bitmap image in pixels.
     * @param height      The height of the bitmap image in pixels.
     * @param transparent Specifies whether the bitmap image supports per-pixel
     *                    transparency. The default value is <code>true</code>
     *                    (transparent). To create a fully transparent bitmap,
     *                    set the value of the <code>transparent</code>
     *                    parameter to <code>true</code> and the value of the
     *                    <code>fillColor</code> parameter to 0x00000000(or to
     *                    0). Setting the <code>transparent</code> property to
     *                    <code>false</code> can result in minor improvements
     *                    in rendering performance.
     * @param fillColor   A 32-bit ARGB color value that you use to fill the
     *                    bitmap image area. The default value is
     *                    0xFFFFFFFF(solid white).
     */
    function SceneImage2D(width, height, transparent, fillColor, powerOfTwo, stage) {
        if (transparent === void 0) { transparent = true; }
        if (fillColor === void 0) { fillColor = 0xffffffff; }
        if (powerOfTwo === void 0) { powerOfTwo = true; }
        if (stage === void 0) { stage = null; }
        return _super.call(this, width, height, transparent, fillColor, powerOfTwo, stage) || this;
    }
    Object.defineProperty(SceneImage2D.prototype, "assetType", {
        /**
         *
         * @returns {string}
         */
        get: function () {
            return SceneImage2D.assetType;
        },
        enumerable: true,
        configurable: true
    });
    SceneImage2D.prototype.createRenderer = function () {
        //create the projection
        var projection = new PerspectiveProjection();
        projection.coordinateSystem = CoordinateSystem.RIGHT_HANDED;
        projection.originX = -1;
        projection.originY = 1;
        //create the view
        SceneImage2D._root = new DisplayObjectContainer();
        SceneImage2D._renderer = RenderGroup.getInstance(new View(projection, this._stage, null, null, null, true), RendererType.DEFAULT).getRenderer(new SceneGraphPartition(SceneImage2D._root));
        SceneImage2D._root.partition = SceneImage2D._renderer.partition;
        //setup the projection
        SceneImage2D._renderer.disableClear = true;
        SceneImage2D._renderer.view.backgroundAlpha = 0;
        SceneImage2D._renderer.view.projection = projection;
        SceneImage2D._renderer.view.projection.transform = new Transform();
        SceneImage2D._renderer.view.projection.transform.moveTo(0, 0, -1000);
        SceneImage2D._renderer.view.projection.transform.lookAt(new Vector3D());
        SceneImage2D._renderer.renderableSorter = null; //new RenderableSort2D();
    };
    SceneImage2D.prototype.createBillboardRenderer = function () {
        //create the projection
        var projection = new PerspectiveProjection();
        projection.coordinateSystem = CoordinateSystem.RIGHT_HANDED;
        projection.originX = -1;
        projection.originY = 1;
        //create the view
        SceneImage2D._billboardRoot = new DisplayObjectContainer();
        SceneImage2D._billboardRenderer = RenderGroup.getInstance(new View(projection, this._stage, null, null, null, true), RendererType.DEFAULT).getRenderer(new SceneGraphPartition(SceneImage2D._billboardRoot));
        SceneImage2D._billboardRoot.partition = SceneImage2D._billboardRenderer.partition;
        //setup the projection
        SceneImage2D._billboardRenderer.disableClear = true;
        SceneImage2D._billboardRenderer.view.backgroundAlpha = 0;
        SceneImage2D._billboardRenderer.view.projection = projection;
        SceneImage2D._billboardRenderer.view.projection.transform = new Transform();
        SceneImage2D._billboardRenderer.view.projection.transform.moveTo(0, 0, -1000);
        SceneImage2D._billboardRenderer.view.projection.transform.lookAt(new Vector3D());
        SceneImage2D._billboardRenderer.renderableSorter = null; //new RenderableSort2D();
        var mat = new MethodMaterial(new BitmapImage2D(128, 128, false, 0x0));
        //mat.colorTransform = new ColorTransform(argb[1]/255, argb[2]/255, argb[3]/255);
        mat.bothSides = true;
        mat.alphaBlending = true;
        mat.alphaPremultiplied = true;
        SceneImage2D._billboard = new Billboard(mat);
        SceneImage2D._billboard.style = new Style();
        SceneImage2D._billboardRoot.addChild(SceneImage2D._billboard);
    };
    /**
     * Frees memory that is used to store the BitmapImage2D object.
     *
     * <p>When the <code>dispose()</code> method is called on an image, the width
     * and height of the image are set to 0. All subsequent calls to methods or
     * properties of this BitmapImage2D instance fail, and an exception is thrown.
     * </p>
     *
     * <p><code>BitmapImage2D.dispose()</code> releases the memory occupied by the
     * actual bitmap data, immediately(a bitmap can consume up to 64 MB of
     * memory). After using <code>BitmapImage2D.dispose()</code>, the BitmapImage2D
     * object is no longer usable and an exception may be thrown if
     * you call functions on the BitmapImage2D object. However,
     * <code>BitmapImage2D.dispose()</code> does not garbage collect the BitmapImage2D
     * object(approximately 128 bytes); the memory occupied by the actual
     * BitmapImage2D object is released at the time the BitmapImage2D object is
     * collected by the garbage collector.</p>
     *
     */
    SceneImage2D.prototype.dispose = function () {
        _super.prototype.dispose.call(this);
        //todo
    };
    /**
     * Fills a rectangular area of pixels with a specified ARGB color.
     *
     * @param rect  The rectangular area to fill.
     * @param color The ARGB color value that fills the area. ARGB colors are
     *              often specified in hexadecimal format; for example,
     *              0xFF336699.
     * @throws TypeError The rect is null.
     */
    SceneImage2D.prototype.fillRect = function (rect, color) {
        if (!SceneImage2D._renderer)
            this.createRenderer();
        //set target and scale value
        SceneImage2D._renderer.view.target = this;
        SceneImage2D._renderer.view.projection.scale = 1000 / this.rect.height;
        //make sure we are setup on view
        SceneImage2D._renderer.view.x = rect.x;
        SceneImage2D._renderer.view.y = rect.y;
        SceneImage2D._renderer.view.width = rect.width;
        SceneImage2D._renderer.view.height = rect.height;
        SceneImage2D._renderer.view.backgroundAlpha = this._transparent ? (color >> 24) / 255 : 1;
        SceneImage2D._renderer.view.backgroundColor = color & 0xFFFFFF;
        SceneImage2D._renderer.view.clear(true, true);
        this._imageDataDirty = true;
    };
    /**
 * Provides a fast routine to perform pixel manipulation between images with
 * no stretching, rotation, or color effects. This method copies a
 * rectangular area of a source image to a rectangular area of the same size
 * at the destination point of the destination BitmapImage2D object.
 *
 * <p>If you include the <code>alphaBitmap</code> and <code>alphaPoint</code>
 * parameters, you can use a secondary image as an alpha source for the
 * source image. If the source image has alpha data, both sets of alpha data
 * are used to composite pixels from the source image to the destination
 * image. The <code>alphaPoint</code> parameter is the point in the alpha
 * image that corresponds to the upper-left corner of the source rectangle.
 * Any pixels outside the intersection of the source image and alpha image
 * are not copied to the destination image.</p>
 *
 * <p>The <code>mergeAlpha</code> property controls whether or not the alpha
 * channel is used when a transparent image is copied onto another
 * transparent image. To copy pixels with the alpha channel data, set the
 * <code>mergeAlpha</code> property to <code>true</code>. By default, the
 * <code>mergeAlpha</code> property is <code>false</code>.</p>
 *
 * @param sourceBitmapImage2D The input bitmap image from which to copy pixels.
 *                         The source image can be a different BitmapImage2D
 *                         instance, or it can refer to the current
 *                         BitmapImage2D instance.
 * @param sourceRect       A rectangle that defines the area of the source
 *                         image to use as input.
 * @param destPoint        The destination point that represents the
 *                         upper-left corner of the rectangular area where
 *                         the new pixels are placed.
 * @param alphaBitmapImage2D  A secondary, alpha BitmapImage2D object source.
 * @param alphaPoint       The point in the alpha BitmapImage2D object source
 *                         that corresponds to the upper-left corner of the
 *                         <code>sourceRect</code> parameter.
 * @param mergeAlpha       To use the alpha channel, set the value to
 *                         <code>true</code>. To copy pixels with no alpha
 *                         channel, set the value to <code>false</code>.
 * @throws TypeError The sourceBitmapImage2D, sourceRect, destPoint are null.
 */
    SceneImage2D.prototype.copyPixels = function (source, sourceRect, destPoint, alphaBitmapData, alphaPoint, mergeAlpha) {
        this._stage.context.setCulling(ContextGLTriangleFace.NONE);
        this._stage.context.setBlendFactors(ContextGLBlendFactor.ONE, ContextGLBlendFactor.ONE_MINUS_SOURCE_ALPHA);
        this._stage.copyPixels(source, this, sourceRect, destPoint, alphaBitmapData, alphaPoint, mergeAlpha);
        this._imageDataDirty = true;
    };
    SceneImage2D.prototype.threshold = function (source, sourceRect, destPoint, operation, threshold, color, mask, copySource) {
        this._stage.context.setCulling(ContextGLTriangleFace.NONE);
        this._stage.context.setBlendFactors(ContextGLBlendFactor.ONE, ContextGLBlendFactor.ZERO);
        this._stage.threshold(source, this, sourceRect, destPoint, operation, threshold, color, mask, copySource);
        this._imageDataDirty = true;
    };
    SceneImage2D.prototype.draw = function (source, matrix, colorTransform, blendMode, clipRect, smoothing) {
        if (source instanceof DisplayObject) {
            if (!SceneImage2D._renderer)
                this.createRenderer();
            var oldParent = source.parent;
            var oldChildIdx = oldParent ? oldParent.getChildIndex(source) : 0;
            var oldMatrix3D = source.transform.matrix3D.clone();
            var oldVisible = source.visible;
            var oldColorTransform = source.transform.colorTransform.clone();
            if (matrix) {
                SceneImage2D._root.transform.scaleTo(matrix.a, -matrix.d, 1);
                SceneImage2D._root.transform.moveTo(matrix.tx, this.rect.height - matrix.ty, 0);
            }
            else {
                SceneImage2D._root.transform.scaleTo(1, -1, 1);
                SceneImage2D._root.transform.moveTo(0, this.rect.height, 0);
            }
            //root.transform.colorTransform = colorTransform;
            SceneImage2D._renderer.view.target = this;
            SceneImage2D._renderer.view.projection.scale = 1000 / this.rect.height;
            SceneImage2D._renderer.view.x = 0;
            SceneImage2D._renderer.view.y = 0;
            SceneImage2D._renderer.view.width = this.width;
            SceneImage2D._renderer.view.height = this.height;
            SceneImage2D._root.removeChildren(0, SceneImage2D._root.numChildren);
            SceneImage2D._root.addChild(source);
            source.transform.matrix3D = new Matrix3D();
            source.visible = true;
            source.transform.colorTransform = null;
            //save snapshot if unlocked
            //if (!this._locked)
            //SceneImage2D.scene.view.target=this;
            //SceneImage2D.scene.renderer.disableClear = !this._locked;
            //render
            SceneImage2D._renderer.render();
            if (oldParent) {
                if (oldParent.adapter && oldParent.adapter != oldParent &&
                    source.adapter && source.adapter != source && oldParent.adapter.addChildAt) {
                    oldParent.adapter.addChildAt(source.adapter, oldChildIdx);
                }
                else {
                    oldParent.addChildAt(source, oldChildIdx);
                }
            }
            source.transform.matrix3D = oldMatrix3D;
            source.visible = oldVisible;
            source.transform.colorTransform = oldColorTransform;
            //SceneImage2D.scene.dispose();
            //SceneImage2D.scene=null;
            this._imageDataDirty = true;
            return;
        }
        if (!SceneImage2D._billboardRenderer)
            this.createBillboardRenderer();
        SceneImage2D._billboardRenderer.disableClear = true;
        SceneImage2D._billboardRenderer.view.target = this;
        SceneImage2D._billboardRenderer.view.projection.scale = 1000 / this.rect.height;
        SceneImage2D._billboard.material.style.image = source;
        if (matrix) {
            SceneImage2D._billboardRoot.transform.scaleTo(matrix.a, -matrix.d, 1);
            SceneImage2D._billboardRoot.transform.moveTo(matrix.tx, this.rect.height - matrix.ty, 0);
        }
        else {
            SceneImage2D._billboardRoot.transform.scaleTo(1, -1, 1);
            SceneImage2D._billboardRoot.transform.moveTo(0, this.rect.height, 0);
        }
        //render
        SceneImage2D._billboardRenderer.render();
        this._imageDataDirty = true;
    };
    SceneImage2D.assetType = "[image SceneImage2D]";
    return SceneImage2D;
}(BitmapImage2D));
export { SceneImage2D };
Stage.registerAbstraction(_Stage_BitmapImage2D, SceneImage2D);
